/**
 * Camada 1c · as credenciais que as capacidades transversais entregam ao transporte.
 *
 * POR QUE ELE É UM `useMemo` E NÃO UM OBJETO ESCRITO NO RENDER, e isto não é preciosismo: o
 * objeto de credenciais é DEPENDÊNCIA DE EFEITO do sino e da fonte de ajuda. Escrito direto no
 * corpo do componente, ele nasce com identidade nova a cada render, o efeito enxerga
 * dependência mudada, dispara de novo, o estado muda, e o render acontece outra vez. É busca
 * em laço, e ela não aparece como erro: aparece como aba que esquenta e serviço que recebe
 * centenas de chamadas por minuto.
 *
 * A ARMADILHA É QUE ELE FUNCIONA. A tela desenha certo, o dado chega, e nada acusa. O defeito
 * só é visto por quem olha a aba de rede ou o log do serviço, e por isso ele mora aqui, num
 * lugar em que ninguém precisa lembrar dele.
 */
import { useMemo } from 'react';

/**
 * O que o transporte precisa saber para falar com um serviço em nome de quem está na tela.
 *
 * É UMA FUNÇÃO E NÃO UM VALOR, de propósito: o token expira e é renovado durante a vida da
 * página. Um valor lido uma vez congelaria o token do primeiro segundo, e as chamadas de meia
 * hora depois iriam com uma credencial vencida, tomando 401 num caminho que funcionava.
 */
export interface Credenciais {
  readonly obter: () => {
    readonly token: string | null;
    readonly tenantId: string | null;
    /**
     * O E-MAIL DE QUEM ESTA NA TELA, e ele NAO e enfeite nem duplicata do token.
     *
     * O DEFEITO QUE ISTO FECHA, medido em 05/09/2026: a primeira versao de
     * `transporte-control-plane.ts` mandava so `Authorization: Bearer`, e o guarda do
     * `admin-api` (`operador.guard.ts`) le `x-operador-email` a CADA requisicao para conferir
     * contra `app.operadores_control_plane`. Sem o cabecalho, a resposta e 401 e o sino do
     * admin ficaria INDISPONIVEL para sempre, sem ninguem entender por que.
     *
     * O CABECALHO NAO E A AUTENTICACAO, e o proprio guarda escreve isso: o token e que prova
     * a identidade, e este campo carrega o e-mail JA VERIFICADO para o guarda nao precisar
     * decodificar o token de novo.
     */
    readonly email: string | null;
  };
}

/**
 * Monta as credenciais uma vez, e só as recria quando token ou cliente realmente mudam.
 *
 * `tenantId` ACEITA `null` E ISSO É LEGÍTIMO: o operador do plano de controle não tem cliente,
 * e inventar um valor de fachada ali é o que faz o `admin-api` recusar com 401. O transporte
 * do plano `control-plane` simplesmente não envia o cabeçalho quando ele é nulo.
 */
export function usarCredenciais(
  token: string | null,
  tenantId: string | null,
  email: string | null,
): Credenciais {
  return useMemo(
    () => ({ obter: () => ({ token, tenantId, email }) }),
    // As dependências são os VALORES, e não o objeto: é o que faz a identidade do resultado
    // mudar apenas quando algo de verdade mudou.
    [token, tenantId, email],
  );
}
